/*
 * File: SalespersonInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 May 2021 8:41:57 am
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Form, Select, Space, Spin, Typography } from 'antd';
import { CreateSalesOrderType } from 'common/enums/CreateSalesOrderType';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetEmployeesQuery } from 'redux/services/useLeftyTheSalesmanService';
import { BlockLoader } from '../BlockLoader';
const { Title } = Typography;

const styles: InlineStylesModel = {
  formItem: {
    marginBottom: 10
  },
  formItemCol: {
    marginRight: '16px',
    maxHeight: '42px'
  },
  titleColor: {
    color: '#2F5596'
  }
};

export const SalespersonInput = (): JSX.Element => {
  const {
    values: { customerId, salesPersonFullName, salesPersonPersonnelNumber },
    setFieldValue
  } = useFormikContext<SalesOrderPayloadModel>();
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const [offset, _setOffset] = useState(0);
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  /* ****************** Hook API ****************** */
  const { data: employeesResponse, isFetching, isLoading } = useGetEmployeesQuery({ overrideSkipTake: true, dataAreaId, offset });

  /* ****************** JSX / Variables ****************** */
  const salesPersonSearchOptions = useMemo(
    () => employeesResponse?.data?.map((employee) => ({ label: `${employee.name} - ${employee.personnelNumber}`, value: `${employee.name} - ${employee.personnelNumber}` })),
    [employeesResponse]
  );

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  // const handleSearch = useDebouncedCallback((value: string | undefined) => {
  //   _setOffset(0);
  // }, 600);

  const handleClear = (): void => {
    _setOffset(0);
    setFieldValue('salesPersonFullName' as keyof SalesOrderPayloadModel, null);
    setFieldValue('salesPersonPersonnelNumber' as keyof SalesOrderPayloadModel, null);
  };

  // const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
  //   const target = event.currentTarget;

  //   if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && employeesResponse && employeesResponse?.totalCount - offset > 25) {
  //     _setOffset(offset + 25);
  //   }
  // };

  const handleSelect = (value: string): void => {
    setFieldValue('salesPersonFullName' as keyof SalesOrderPayloadModel, value.split(' - ')[0]);
    setFieldValue('salesPersonPersonnelNumber' as keyof SalesOrderPayloadModel, value.split(' - ')[1]);
  };

  const isInvalid = !customerId;

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      (isLoading || isFetching) && offset ? (
        <div style={{ padding: '5px 10px' }}>
          <Space>
            <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ****************** Render ****************** */
  return (
    <>
      <Title level={5} style={styles.titleColor}>
        {CreateSalesOrderType.SALESPERSON}
      </Title>
      <Form.Item
        style={styles.formItem}
        validateStatus={isInvalid ? 'error' : undefined}
        help={customerId && !salesPersonFullName ? `No salesperson found for customer ${customerId}` : isInvalid ? 'Customer selection required' : undefined}>
        <Select
          value={salesPersonFullName ? `${salesPersonFullName} - ${salesPersonPersonnelNumber}` : undefined}
          allowClear
          disabled={isInvalid || isPosted}
          dropdownRender={dropDownRender}
          // filterOption={false}
          filterOption={(input, option): boolean => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
          loading={isLoading || isFetching}
          onClear={handleClear}
          // onPopupScroll={handleScroll}
          // onSearch={handleSearch}
          onSelect={handleSelect}
          options={salesPersonSearchOptions}
          showSearch
        />
      </Form.Item>
    </>
  );
};
